import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CHANNEL_PERMISSION_MASK,
  PERMISSION_GROUPS,
  PERMISSION_INFO,
  Permission,
  computeChannelPermissions,
  hasPermission,
  type Channel,
  type CommunityStructure,
  type MemberOverwrite,
  type PermissionOverwrite,
  type UserSummary,
  FORUM_TAG_MAX,
  ALL_PERMISSIONS,
  samePermissions,
} from "@atrium/shared";
import { Popover } from "radix-ui";
import { api, errorMessage } from "../../lib/api";
import { cn } from "../../lib/cn";
import { useIsDesktop } from "../../lib/hooks";
import { Button, IconButton } from "../../components/ui/Button";
import { Switch } from "../../components/ui/Controls";
import { confirm } from "../../components/ui/Dialog";
import { Field, Input, Textarea } from "../../components/ui/Input";
import { Menu, MenuContent, MenuItem, MenuLabel, MenuTrigger } from "../../components/ui/Menu";
import { Select, SettingsGroup, SettingsLayout, SettingsRow, UnsavedBar } from "../../components/ui/Settings";
import { RowSkeleton } from "../../components/ui/Skeleton";
import { toast } from "../../components/ui/Toast";
import { Tooltip } from "../../components/ui/Tooltip";
import { IconCheck, IconCopy, IconDrag, IconInfo, IconLink, IconPlus, IconTrash, IconUserPlus, IconX } from "../../components/icons";
import { SortableList } from "../../components/ui/Sortable";
import { Avatar } from "../../components/ui/Avatar";
import type { Member } from "@atrium/shared";
import { copyText } from "../posts/usePostActions";
import { channelIcon } from "./ChannelSidebar";
import { RoleChecklist } from "./ChannelDialogs";
import { permsOf, useMembers } from "./hooks";
import { useRefreshCommunity } from "./settings/shared";
import { canSync, categoryTemplate, offerPermissionSync, syncChannel } from "./permissionSync";

/*
 * Settings for a single channel, as its own full-screen view (like Discord):
 * Overview, Permissions and Invites, with Delete channel at the bottom of the sidebar.
 */

type Section = "overview" | "permissions" | "invites";

const SLOW_MODES = [
  { value: "0", label: "Off" },
  { value: "5", label: "5 seconds" },
  { value: "10", label: "10 seconds" },
  { value: "15", label: "15 seconds" },
  { value: "30", label: "30 seconds" },
  { value: "60", label: "1 minute" },
  { value: "120", label: "2 minutes" },
  { value: "300", label: "5 minutes" },
  { value: "600", label: "10 minutes" },
  { value: "900", label: "15 minutes" },
  { value: "1800", label: "30 minutes" },
  { value: "3600", label: "1 hour" },
  { value: "7200", label: "2 hours" },
  { value: "21600", label: "6 hours" },
];

interface Draft {
  name: string;
  topic: string;
  categoryId: string | null;
  slowModeSeconds: number;
  userLimit: number;
  type: Channel["type"];
  ageRestricted: boolean;
  isPrivate: boolean;
  allowedRoleIds: string[];
  overwrites: PermissionOverwrite[];
  memberOverwrites: MemberOverwrite[];
  forumTags: Channel["forumTags"];
}

const draftOf = (c: Channel): Draft => ({
  memberOverwrites: (c.memberOverwrites ?? []).map(({ userId, allow, deny }) => ({ userId, allow, deny })),
  forumTags: c.forumTags,
  name: c.name,
  topic: c.topic,
  categoryId: c.categoryId,
  slowModeSeconds: c.slowModeSeconds,
  userLimit: c.userLimit,
  type: c.type,
  ageRestricted: c.ageRestricted,
  isPrivate: c.isPrivate,
  allowedRoleIds: c.allowedRoleIds,
  overwrites: c.overwrites ?? [],
});

export function ChannelSettings({ structure, channel, section: sectionParam }: { structure: CommunityStructure; channel: Channel; section?: string }) {
  const navigate = useNavigate();
  const desktop = useIsDesktop();
  const refresh = useRefreshCommunity(structure.community.id);
  const qc = useQueryClient();
  const c = structure.community;
  const perms = permsOf(c.myPermissions, c.isOwner);
  const base = `/c/${c.id}/${channel.id}/settings`;
  const source = useMemo(() => draftOf(channel), [channel]);
  const [draft, setDraft] = useState<Draft>(source);
  const [saving, setSaving] = useState(false);
  useEffect(() => setDraft(source), [source]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(source);
  const Icon = channelIcon(channel);

  const items = [
    perms.manageChannels && { id: "overview" as const, label: "Overview", keywords: "name topic slow mode category age announcement limit" },
    (perms.manageRoles || perms.manageChannels) && { id: "permissions" as const, label: "Permissions", keywords: "private roles overrides access" },
    (perms.invite || perms.manageInvites || perms.manageCommunity) && channel.type !== "voice" && !channel.isPrivate && { id: "invites" as const, label: "Invites", keywords: "links" },
  ].filter(Boolean) as { id: Section; label: string; keywords: string }[];
  const section = (items.some((i) => i.id === sectionParam) ? sectionParam : desktop ? items[0]?.id : undefined) as Section | undefined;

  const close = async () => {
    if (dirty && !(await confirm({ title: "Discard changes?", description: "You have unsaved changes to this channel.", confirmLabel: "Discard", danger: true }))) return;
    navigate(`/c/${c.id}/${channel.id}`);
  };

  const save = async () => {
    setSaving(true);
    try {
      const body: Record<string, unknown> = {};
      for (const k of Object.keys(draft) as (keyof Draft)[]) if (JSON.stringify(draft[k]) !== JSON.stringify(source[k])) body[k] = draft[k];
      // Empty member overrides only exist so a newly added member stays listed; don't send them.
      if (body.memberOverwrites) body.memberOverwrites = draft.memberOverwrites.filter((o) => o.allow || o.deny);
      if (body.forumTags) body.forumTags = draft.forumTags.filter((t) => t.name.trim()).map((t) => ({ ...t, name: t.name.trim(), emoji: t.emoji?.trim() || undefined }));
      await api.patch(`/api/channels/${channel.id}`, body);
      await refresh();
      toast.show("Channel saved");
      // Moved to another category: offer to take on its permissions.
      if (body.categoryId) {
        const fresh = qc.getQueryData<CommunityStructure>(["structure", c.id]);
        const moved = fresh?.channels.find((x) => x.id === channel.id);
        if (fresh && moved) await offerPermissionSync(qc, fresh, moved, moved.categoryId);
      }
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    const ok = await confirm({
      title: `Delete ${channel.type === "voice" ? "" : "#"}${channel.name}?`,
      description: "All messages in this channel will be permanently deleted. This can't be undone.",
      confirmLabel: "Delete channel",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.del(`/api/channels/${channel.id}`);
      await refresh();
      navigate(`/c/${c.id}`, { replace: true });
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  if (!items.length) return null;

  return (
    <SettingsLayout<Section>
      title={channel.name}
      search={false}
      header={
        <div className="flex h-10 items-center gap-1.5 px-1.5 text-sm">
          <Icon className="icon size-4 shrink-0 text-fg-3" aria-hidden />
          <span className="truncate font-semibold">{draft.name || channel.name}</span>
          <span className="shrink-0 text-fg-3" aria-hidden>
            ·
          </span>
          <span className="truncate text-xs text-fg-3">{c.name}</span>
        </div>
      }
      groups={[{ items }]}
      active={section}
      onSelect={(s) => navigate(`${base}/${s}`, { replace: desktop })}
      onBack={() => navigate(base)}
      onClose={() => void close()}
      sectionTitle={section ? items.find((i) => i.id === section)?.label : undefined}
      wide={section === "permissions"}
      footer={
        perms.manageChannels ? (
          <button onClick={() => void remove()} className="flex h-9 w-full items-center gap-2.5 rounded-md px-2 text-sm text-danger hover:bg-danger-soft">
            <span className="flex-1 text-left">Delete channel</span>
            <IconTrash className="icon size-[18px]" aria-hidden />
          </button>
        ) : undefined
      }
    >
      {section === "overview" && <OverviewSection structure={structure} channel={channel} draft={draft} setDraft={setDraft} />}
      {section === "permissions" && (
        <PermissionsEditor
          structure={structure}
          draft={draft}
          setDraft={(patch) => setDraft({ ...draft, ...patch })}
          canEditOverrides={perms.manageRoles}
          known={channel.memberOverwrites}
          voice={channel.type === "voice" || channel.type === "stage"}
          noun="channel"
          banner={<SyncBanner structure={structure} channel={channel} draft={draft} dirty={dirty} />}
        />
      )}
      {section === "invites" && <InvitesSection structure={structure} channel={channel} />}
      {section !== "invites" && <UnsavedBar show={dirty} saving={saving} onReset={() => setDraft(source)} onSave={() => void save()} />}
    </SettingsLayout>
  );
}

/* ───────────── Overview ───────────── */

function OverviewSection({ structure, channel, draft, setDraft }: { structure: CommunityStructure; channel: Channel; draft: Draft; setDraft: (d: Draft) => void }) {
  const voice = channel.type === "voice";
  return (
    <div className="space-y-7">
      <Field label="Channel name">{(p) => <Input {...p} value={draft.name} maxLength={32} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />}</Field>
      {!voice && (
        <Field label="Channel topic" counter={`${1024 - draft.topic.length}`}>
          {(p) => (
            <Textarea {...p} rows={5} value={draft.topic} maxLength={1024} placeholder="Let everyone know how to use this channel" onChange={(e) => setDraft({ ...draft, topic: e.target.value })} />
          )}
        </Field>
      )}

      <SettingsGroup>
        <SettingsRow title="Slow mode" description="Members can send one message per interval, unless they have Bypass slow mode." stack>
          <Select aria-label="Slow mode" className="w-full" value={String(draft.slowModeSeconds)} onChange={(v) => setDraft({ ...draft, slowModeSeconds: Number(v) })} options={SLOW_MODES} />
        </SettingsRow>
        <SettingsRow title="Category" stack>
          <Select
            aria-label="Category"
            className="w-full"
            value={draft.categoryId ?? ""}
            onChange={(v) => setDraft({ ...draft, categoryId: v || null })}
            options={[{ value: "", label: "No category" }, ...structure.categories.map((cat) => ({ value: cat.id, label: cat.name }))]}
          />
        </SettingsRow>
        {voice && (
          <SettingsRow title="User limit" description={draft.userLimit ? `Up to ${draft.userLimit} people at once.` : "No limit beyond the room maximum of 12."}>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={0}
                max={12}
                value={Math.min(draft.userLimit, 12)}
                onChange={(e) => setDraft({ ...draft, userLimit: Number(e.target.value) })}
                aria-label="User limit"
                className="w-40 accent-[var(--accent)]"
              />
              <span className="w-8 text-right text-sm tabular">{draft.userLimit || "∞"}</span>
            </div>
          </SettingsRow>
        )}
      </SettingsGroup>

      <SettingsGroup title="Content">
        <SettingsRow title="Age-restricted" description="Members confirm they're over 18 before they can see this channel's content." htmlFor="ch-age">
          <Switch id="ch-age" label="Age-restricted" checked={draft.ageRestricted} onCheckedChange={(v) => setDraft({ ...draft, ageRestricted: v })} />
        </SettingsRow>
        {(channel.type === "text" || channel.type === "announcement") && (
          <SettingsRow
            title="Announcement channel"
            description="Only roles with Post announcements can send messages here, and messages can be published to the feed as the community."
            htmlFor="ch-ann"
          >
            <Switch id="ch-ann" label="Announcement channel" checked={draft.type === "announcement"} onCheckedChange={(v) => setDraft({ ...draft, type: v ? "announcement" : "text" })} />
          </SettingsRow>
        )}
      </SettingsGroup>

      {(channel.type === "forum" || channel.type === "media") && <ForumTagsEditor draft={draft} setDraft={setDraft} />}
    </div>
  );
}

function ForumTagsEditor({ draft, setDraft }: { draft: Draft; setDraft: (d: Draft) => void }) {
  const tags = draft.forumTags;
  const set = (i: number, patch: Partial<Draft["forumTags"][number]>) => setDraft({ ...draft, forumTags: tags.map((t, j) => (j === i ? { ...t, ...patch } : t)) });
  const add = () => setDraft({ ...draft, forumTags: [...tags, { id: Math.random().toString(36).slice(2, 10), name: "" }] });
  const move = (id: string, to: number) => {
    const next = [...tags];
    const [t] = next.splice(next.findIndex((x) => x.id === id), 1);
    next.splice(to, 0, t);
    setDraft({ ...draft, forumTags: next });
  };
  return (
    <SettingsGroup
      title="Post tags"
      description="Members pick up to five when posting, and can filter posts by them. Drag to set the order they show in."
      action={
        <Button size="sm" variant="ghost" icon={IconPlus} onClick={add} disabled={tags.length >= FORUM_TAG_MAX}>
          Add tag
        </Button>
      }
    >
      {tags.length === 0 ? (
        <p className="px-4 py-3 text-sm text-fg-3">No tags yet.</p>
      ) : (
        <SortableList
          label="Post tags, in display order"
          items={tags}
          getId={(t) => t.id}
          canMove={() => true}
          onMove={move}
          renderItem={(t, { handle, dragging }) => {
            const i = tags.indexOf(t);
            return (
              <div className={cn("flex items-center gap-2 py-2 pl-2 pr-4", dragging && "rounded-md bg-surface")}>
                <span
                  onPointerDown={handle.onPointerDown}
                  onKeyDown={handle.onKeyDown}
                  style={handle.style}
                  tabIndex={0}
                  data-sort-handle
                  aria-roledescription={handle["aria-roledescription"]}
                  aria-label={`Reorder ${t.name || "tag"} (Alt + arrow keys)`}
                  className="flex h-8 w-5 shrink-0 items-center justify-center rounded text-fg-3 hover:text-fg"
                >
                  <IconDrag className="icon size-3.5" />
                </span>
                <Input value={t.emoji ?? ""} onChange={(e) => set(i, { emoji: e.target.value })} maxLength={16} placeholder="🙂" aria-label="Tag emoji" className="!h-8 !w-12 shrink-0 !px-1 text-center" />
                <Input value={t.name} onChange={(e) => set(i, { name: e.target.value })} maxLength={20} placeholder="Tag name" aria-label="Tag name" className="!h-8 flex-1" autoFocus={!t.name} />
                <IconButton size="sm" label="Remove tag" icon={IconTrash} tone="danger" onClick={() => setDraft({ ...draft, forumTags: tags.filter((_, j) => j !== i) })} />
              </div>
            );
          }}
        />
      )}
    </SettingsGroup>
  );
}

/* ───────────── Invites ───────────── */

interface InviteRow {
  code: string;
  inviter: UserSummary | null;
  channelId: string | null;
  uses: number;
  maxUses: number;
  expiresAt: string | null;
  expired: boolean;
}

const EXPIRY = [
  { value: "1", label: "1 hour" },
  { value: "24", label: "1 day" },
  { value: "168", label: "7 days" },
  { value: "720", label: "30 days" },
  { value: "0", label: "Never" },
];
const USES = [
  { value: "0", label: "No limit" },
  { value: "1", label: "1 use" },
  { value: "5", label: "5 uses" },
  { value: "10", label: "10 uses" },
  { value: "25", label: "25 uses" },
  { value: "100", label: "100 uses" },
];

function InvitesSection({ structure, channel }: { structure: CommunityStructure; channel: Channel }) {
  const c = structure.community;
  const perms = permsOf(c.myPermissions, c.isOwner);
  const canList = perms.manageInvites || perms.manageCommunity;
  const [expiry, setExpiry] = useState("168");
  const [uses, setUses] = useState("0");
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<string | null>(null);
  const list = useQuery({
    queryKey: ["invites", c.id, channel.id],
    queryFn: () => api.get<{ items: InviteRow[] }>(`/api/communities/${c.id}/invites?channelId=${channel.id}`),
    enabled: canList,
  });
  const create = async () => {
    setCreating(true);
    try {
      const r = await api.post<{ code: string }>(`/api/communities/${c.id}/invites`, { channelId: channel.id, maxUses: Number(uses), expiresInHours: Number(expiry) });
      setCreated(`${window.location.origin}/invite/${r.code}`);
      void list.refetch();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setCreating(false);
    }
  };
  const revoke = async (code: string) => {
    try {
      await api.del(`/api/invites/${code}`);
      void list.refetch();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  return (
    <div>
      {perms.invite && (
        <SettingsGroup title="New invite" description={`People who join with it land straight in #${channel.name}.`}>
          <SettingsRow title="Expires after">
            <Select aria-label="Expires after" value={expiry} onChange={setExpiry} options={EXPIRY} />
          </SettingsRow>
          <SettingsRow title="Maximum uses">
            <Select aria-label="Maximum uses" value={uses} onChange={setUses} options={USES} />
          </SettingsRow>
          <div className="flex items-center gap-2 px-4 py-3">
            {created ? (
              <>
                <Input readOnly value={created} aria-label="Invite link" className="!h-9 font-mono text-sm" onFocus={(e) => e.target.select()} />
                <Button
                  icon={IconCopy}
                  onClick={() => {
                    void copyText(created);
                    toast.show("Link copied");
                  }}
                >
                  Copy
                </Button>
              </>
            ) : (
              <Button variant="primary" icon={IconLink} loading={creating} onClick={() => void create()}>
                Create invite link
              </Button>
            )}
          </div>
        </SettingsGroup>
      )}
      {canList && (
        <SettingsGroup title="Invites to this channel">
          {list.isPending && <RowSkeleton />}
          {list.data?.items.map((i) => (
            <div key={i.code} className={cn("flex items-center gap-3 px-4 py-2.5", i.expired && "opacity-50")}>
              <span className="font-mono text-sm">{i.code}</span>
              <span className="min-w-0 flex-1 truncate text-xs text-fg-3 tabular">
                {i.inviter ? `@${i.inviter.username} · ` : ""}
                {i.uses}
                {i.maxUses ? `/${i.maxUses}` : ""} uses · {i.expired ? "expired" : i.expiresAt ? `expires ${new Date(i.expiresAt).toLocaleDateString("en-GB")}` : "never expires"}
              </span>
              <IconButton
                size="sm"
                label="Copy link"
                icon={IconCopy}
                onClick={() => {
                  void copyText(`${window.location.origin}/invite/${i.code}`);
                  toast.show("Link copied");
                }}
              />
              <IconButton size="sm" label="Revoke invite" icon={IconTrash} tone="danger" onClick={() => void revoke(i.code)} />
            </div>
          ))}
          {list.data && list.data.items.length === 0 && <p className="px-4 py-3 text-sm text-fg-3">No invites point to this channel yet.</p>}
        </SettingsGroup>
      )}
    </div>
  );
}

/* ───────────── Category sync ───────────── */

/** Above a channel's permissions: whether they match its category's, with "Sync now" when they don't. */
function SyncBanner({ structure, channel, draft, dirty }: { structure: CommunityStructure; channel: Channel; draft: Draft; dirty: boolean }) {
  const qc = useQueryClient();
  const category = structure.categories.find((x) => x.id === channel.categoryId);
  if (!category || category.overwrites === undefined) return null;
  const synced = samePermissions(draft, categoryTemplate(category));
  const sync = async () => {
    if (dirty && !(await confirm({ title: "Discard changes and sync?", description: `Your unsaved changes are replaced by ${category.name}'s permissions.`, confirmLabel: "Sync now" }))) return;
    await syncChannel(qc, structure.community.id, channel.id);
  };
  return (
    <div className={cn("flex items-center gap-3 rounded-lg border px-4 py-3", synced ? "border-line bg-surface" : "border-[color-mix(in_srgb,var(--warning)_40%,transparent)] bg-[color-mix(in_srgb,var(--warning)_10%,transparent)]")}>
      {synced ? <IconCheck className="icon size-5 shrink-0 text-success" aria-hidden /> : <IconInfo className="icon size-5 shrink-0 text-warning" aria-hidden />}
      <p className="min-w-0 flex-1 text-sm">
        {synced ? (
          <>
            Permissions synced with <span className="font-semibold">{category.name}</span>. Changes to the category apply here too.
          </>
        ) : (
          <>
            Permissions not synced with <span className="font-semibold">{category.name}</span>. Changes to the category don't apply here.
          </>
        )}
      </p>
      {!synced && canSync(structure, category) && (
        <Button size="sm" onClick={() => void sync()}>
          Sync now
        </Button>
      )}
    </div>
  );
}

/* ───────────── Overrides ───────────── */

type Tri = "allow" | "inherit" | "deny";

function TriToggle({ value, onChange, disabled, label }: { value: Tri; onChange: (v: Tri) => void; disabled?: boolean; label: string }) {
  const opts: { v: Tri; icon: React.ComponentType<{ className?: string }>; tip: string; on: string }[] = [
    { v: "deny", icon: IconX, tip: "Deny", on: "bg-danger text-white" },
    { v: "inherit", icon: () => <span className="text-base leading-none" aria-hidden>/</span>, tip: "Inherit from role", on: "bg-line-strong text-fg" },
    { v: "allow", icon: IconCheck, tip: "Allow", on: "bg-success text-white" },
  ];
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex overflow-hidden rounded-md border border-line-strong">
      {opts.map((o) => (
        <Tooltip key={o.v} label={o.tip}>
          <button
            role="radio"
            aria-checked={value === o.v}
            aria-label={o.tip}
            disabled={disabled}
            onClick={() => onChange(o.v)}
            className={cn("flex h-7 w-8 items-center justify-center text-fg-3 transition-colors disabled:opacity-40", value === o.v ? o.on : "hover:bg-surface hover:text-fg")}
          >
            <o.icon className="icon size-4" />
          </button>
        </Tooltip>
      ))}
    </div>
  );
}

type Target = { kind: "role"; id: string } | { kind: "member"; id: string };

/** "Add a member": search the member list and pick someone to give their own override. */
function MemberPicker({ members, exclude, onPick }: { members: Member[]; exclude: Set<string>; onPick: (u: UserSummary) => void }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const needle = q.trim().toLowerCase();
  const matches = members
    .filter((m) => !exclude.has(m.user.id) && (!needle || m.user.displayName.toLowerCase().includes(needle) || m.user.username.toLowerCase().includes(needle)))
    .slice(0, 8);
  return (
    <Popover.Root
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQ("");
      }}
    >
      <Popover.Trigger asChild>
        <Button size="sm" variant="ghost" icon={IconUserPlus} className="w-full justify-start">
          Add a member
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="start" sideOffset={6} collisionPadding={8} className="z-[60] w-72 rounded-lg bg-surface p-1 shadow-pop animate-pop-in">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search members"
            aria-label="Search members"
            className="!h-9"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter" && matches[0]) {
                e.preventDefault();
                onPick(matches[0].user);
                setOpen(false);
              }
            }}
          />
          <ul className="mt-1 max-h-72 overflow-y-auto" role="listbox" aria-label="Members">
            {matches.map((m) => (
              <li key={m.user.id}>
                <button
                  role="option"
                  aria-selected={false}
                  onClick={() => {
                    onPick(m.user);
                    setOpen(false);
                  }}
                  className="flex h-10 w-full items-center gap-2.5 rounded-md px-2 text-left text-sm hover:bg-surface-2"
                >
                  <Avatar src={m.user.avatarUrl} name={m.user.displayName} size="sm" />
                  <span className="min-w-0 flex-1 truncate">{m.user.displayName}</span>
                  <span className="truncate text-xs text-fg-3">@{m.user.username}</span>
                </button>
              </li>
            ))}
            {matches.length === 0 && <li className="px-2 py-3 text-sm text-fg-3">No members found</li>}
          </ul>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/** What the permissions editor edits: a channel's access, or a category's template for its channels. */
export interface PermissionDraft {
  isPrivate: boolean;
  allowedRoleIds: string[];
  overwrites: PermissionOverwrite[];
  memberOverwrites: MemberOverwrite[];
}

/**
 * Access and overrides for a channel or a category. `known` supplies names
 * for members who already have an override; `voice` includes the voice
 * permissions; `banner` sits above everything (e.g. the sync status).
 */
export function PermissionsEditor({
  structure,
  draft,
  setDraft,
  canEditOverrides,
  known,
  voice,
  noun,
  banner,
}: {
  structure: CommunityStructure;
  draft: PermissionDraft;
  setDraft: (patch: Partial<PermissionDraft>) => void;
  canEditOverrides: boolean;
  known?: (MemberOverwrite & { user: UserSummary | null })[];
  voice: boolean;
  noun: "channel" | "category";
  banner?: ReactNode;
}) {
  const everyone = structure.roles.find((r) => r.isDefault)!;
  const withOverrides = new Set(draft.overwrites.map((o) => o.roleId));
  const listed = [everyone, ...structure.roles.filter((r) => !r.isDefault && withOverrides.has(r.id))];
  const addable = structure.roles.filter((r) => !r.isDefault && !withOverrides.has(r.id));
  const [target, setTarget] = useState<Target>({ kind: "role", id: everyone.id });
  const { data: memberPages } = useMembers(structure.community.id);
  const members = useMemo(() => memberPages?.pages.flatMap((p) => p.items) ?? [], [memberPages]);
  // Names for listed members: from the channel's override list, then the member list.
  const users = useMemo(() => {
    const map = new Map<string, UserSummary>();
    for (const o of known ?? []) if (o.user) map.set(o.userId, o.user);
    for (const m of members) map.set(m.user.id, m.user);
    return map;
  }, [known, members]);
  const memberTarget = target.kind === "member" && draft.memberOverwrites.some((o) => o.userId === target.id) ? target.id : null;
  const role = (!memberTarget && structure.roles.find((r) => r.id === target.id)) || everyone;
  const ow = memberTarget
    ? (draft.memberOverwrites.find((o) => o.userId === memberTarget) ?? { allow: 0, deny: 0 })
    : (draft.overwrites.find((o) => o.roleId === role.id) ?? { allow: 0, deny: 0 });
  const admin = structure.community.isOwner || hasPermission(structure.community.myPermissions, Permission.ADMINISTRATOR);

  const setRoleOverwrite = (next: PermissionOverwrite) => {
    const rest = draft.overwrites.filter((o) => o.roleId !== next.roleId);
    // Keep an (empty) entry for selected non-default roles so they stay in the list until removed.
    setDraft({ overwrites: next.allow || next.deny || next.roleId !== everyone.id ? [...rest, next] : rest });
  };
  // Members keep an (empty) entry while listed too; empty ones are dropped when saving.
  const setMemberOverwrite = (next: MemberOverwrite) =>
    setDraft({ memberOverwrites: draft.memberOverwrites.map((o) => (o.userId === next.userId ? next : o)) });
  const setOverwrite = (allow: number, deny: number) =>
    memberTarget ? setMemberOverwrite({ userId: memberTarget, allow, deny }) : setRoleOverwrite({ roleId: role.id, allow, deny });
  const triOf = (flag: number): Tri => ((ow.allow & flag) === flag ? "allow" : (ow.deny & flag) === flag ? "deny" : "inherit");
  const setTri = (flag: number, v: Tri) => setOverwrite(v === "allow" ? ow.allow | flag : ow.allow & ~flag, v === "deny" ? ow.deny | flag : ow.deny & ~flag);
  const addMember = (u: UserSummary) => {
    if (!draft.memberOverwrites.some((o) => o.userId === u.id)) setDraft({ memberOverwrites: [...draft.memberOverwrites, { userId: u.id, allow: 0, deny: 0 }] });
    setTarget({ kind: "member", id: u.id });
  };
  const removeMember = (userId: string) => {
    setDraft({ memberOverwrites: draft.memberOverwrites.filter((o) => o.userId !== userId) });
    setTarget({ kind: "role", id: everyone.id });
  };

  // Preview: what someone with only @everyone (+ this role), or this member with their roles, ends up with here.
  const member = memberTarget ? members.find((m) => m.user.id === memberTarget) : undefined;
  const memberRoles = member ? structure.roles.filter((r) => !r.isDefault && member.roleIds.includes(r.id)) : [];
  const effective = member?.isOwner
    ? ALL_PERMISSIONS
    : computeChannelPermissions({
        base: everyone.permissions | (memberTarget ? memberRoles.reduce((p, r) => p | r.permissions, 0) : role.isDefault ? 0 : role.permissions),
        bypass: false,
        defaultRoleId: everyone.id,
        memberRoleIds: memberTarget ? [everyone.id, ...memberRoles.map((r) => r.id)] : role.isDefault ? [everyone.id] : [everyone.id, role.id],
        overwrites: draft.overwrites,
        memberOverwrite: memberTarget ? ow : null,
      });
  const relevant = PERMISSION_INFO.filter((p) => p.channel && (voice || p.group !== "Voice"));
  const targetName = memberTarget ? (users.get(memberTarget)?.displayName ?? "Member") : role.isDefault ? "@everyone" : role.name;

  return (
    <div className="space-y-6">
      {banner}
      <SettingsGroup title="Access">
        <SettingsRow
          title={noun === "category" ? "Private category" : "Private channel"}
          description={
            noun === "category"
              ? "Synced channels are only visible to selected roles, members allowed below, administrators and the owner."
              : "Only selected roles, members allowed below, administrators and the owner can see it."
          }
          htmlFor="cs-private"
        >
          <Switch id="cs-private" label={noun === "category" ? "Private category" : "Private channel"} checked={draft.isPrivate} onCheckedChange={(v) => setDraft({ isPrivate: v })} />
        </SettingsRow>
        {draft.isPrivate && (
          <div className="px-4 py-3">
            <RoleChecklist roles={structure.roles} value={draft.allowedRoleIds} onChange={(v) => setDraft({ allowedRoleIds: v })} />
          </div>
        )}
      </SettingsGroup>

      <div>
        <h3 className="mb-1 text-sm font-semibold text-fg-2">Overrides</h3>
        <p className="mb-3 text-xs text-fg-3">
          {noun === "category" ? "Allow or deny permissions in every channel synced with this category." : "Allow or deny permissions in this channel only."} @everyone applies first, then the
          combined overrides of a member's other roles, then a member's own override.
          {!canEditOverrides && " You need Manage roles to change overrides."}
        </p>
        <div className="flex flex-col gap-6 md:flex-row">
          <div className="shrink-0 md:w-56">
            <ul className="space-y-px" aria-label="Roles with overrides">
              {listed.map((r) => {
                const active = !memberTarget && role.id === r.id;
                return (
                  <li key={r.id}>
                    <button
                      onClick={() => setTarget({ kind: "role", id: r.id })}
                      aria-current={active ? "true" : undefined}
                      className={cn("flex h-9 w-full items-center gap-2 rounded-md px-2 text-left text-sm", active ? "bg-surface-2 text-fg" : "text-fg-2 hover:bg-surface")}
                    >
                      <span className="size-2.5 shrink-0 rounded-full" style={{ background: r.color ?? "var(--fg-3)" }} aria-hidden />
                      <span className="min-w-0 flex-1 truncate">{r.isDefault ? "@everyone" : r.name}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {draft.memberOverwrites.length > 0 && (
              <ul className="mt-2 space-y-px border-t border-line pt-2" aria-label="Members with overrides">
                {draft.memberOverwrites.map((o) => {
                  const u = users.get(o.userId);
                  const active = memberTarget === o.userId;
                  return (
                    <li key={o.userId}>
                      <button
                        onClick={() => setTarget({ kind: "member", id: o.userId })}
                        aria-current={active ? "true" : undefined}
                        className={cn("flex h-9 w-full items-center gap-2 rounded-md px-2 text-left text-sm", active ? "bg-surface-2 text-fg" : "text-fg-2 hover:bg-surface")}
                      >
                        <Avatar src={u?.avatarUrl ?? null} name={u?.displayName ?? "?"} size="xs" />
                        <span className="min-w-0 flex-1 truncate">{u?.displayName ?? "Unknown member"}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            {canEditOverrides && (
              <div className="mt-2 flex flex-col gap-0.5">
                {addable.length > 0 && (
                  <Menu>
                    <MenuTrigger asChild>
                      <Button size="sm" variant="ghost" icon={IconPlus} className="w-full justify-start">
                        Add a role
                      </Button>
                    </MenuTrigger>
                    <MenuContent align="start">
                      <MenuLabel>Override permissions for</MenuLabel>
                      {addable.map((r) => (
                        <MenuItem
                          key={r.id}
                          onSelect={() => {
                            setDraft({ overwrites: [...draft.overwrites, { roleId: r.id, allow: 0, deny: 0 }] });
                            setTarget({ kind: "role", id: r.id });
                          }}
                        >
                          <span className="flex items-center gap-2">
                            <span className="size-2.5 rounded-full" style={{ background: r.color ?? "var(--fg-3)" }} aria-hidden />
                            {r.name}
                          </span>
                        </MenuItem>
                      ))}
                    </MenuContent>
                  </Menu>
                )}
                <MemberPicker members={members} exclude={new Set(draft.memberOverwrites.map((o) => o.userId))} onPick={addMember} />
              </div>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="mb-3 flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2 text-sm font-medium">
                {memberTarget && <Avatar src={users.get(memberTarget)?.avatarUrl ?? null} name={targetName} size="xs" />}
                <span className="truncate">{targetName}</span>
              </div>
              {canEditOverrides && (
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setOverwrite(0, 0)}>
                    Clear
                  </Button>
                  {memberTarget ? (
                    <Button size="sm" variant="ghost" className="text-danger hover:bg-danger-soft hover:text-danger" onClick={() => removeMember(memberTarget)}>
                      Remove member
                    </Button>
                  ) : (
                    !role.isDefault && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-danger hover:bg-danger-soft hover:text-danger"
                        onClick={() => {
                          setDraft({ overwrites: draft.overwrites.filter((o) => o.roleId !== role.id) });
                          setTarget({ kind: "role", id: everyone.id });
                        }}
                      >
                        Remove role
                      </Button>
                    )
                  )}
                </div>
              )}
            </div>
            {PERMISSION_GROUPS.map((g) => {
              const items = relevant.filter((p) => p.group === g);
              if (!items.length) return null;
              return (
                <SettingsGroup key={g} title={g === "Text" ? "Text" : g}>
                  {items.map((p) => {
                    const flag = Permission[p.key];
                    const lacking = !admin && (structure.community.myPermissions & flag) !== flag;
                    const result = hasPermission(effective, flag);
                    return (
                      <SettingsRow
                        key={p.key}
                        title={p.label}
                        description={
                          <>
                            {p.description}{" "}
                            <span className={result ? "text-success" : "text-fg-3"}>{result ? "Allowed here." : "Not allowed here."}</span>
                          </>
                        }
                      >
                        <TriToggle label={p.label} value={triOf(flag)} disabled={!canEditOverrides || (lacking && triOf(flag) !== "allow")} onChange={(v) => setTri(flag, v)} />
                      </SettingsRow>
                    );
                  })}
                </SettingsGroup>
              );
            })}
            <p className="text-xs text-fg-3">
              Owners and administrators ignore overrides. Only channel-level permissions are listed ({PERMISSION_INFO.filter((p) => (CHANNEL_PERMISSION_MASK & Permission[p.key]) !== 0).length} of {PERMISSION_INFO.length}).
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

